import React, { useEffect, useRef, useState } from 'react';
import { KIND_BACKUP, KIND_TRANSFER, bytesToBase64 } from './deviceTransfer';

/**
 * Д-3 — панель «Копия и перенос» на СТАРОМ устройстве участника (дизайн §4).
 *
 * Две кнопки, как решил владелец: «Сохранить копию переписки» (устройство
 * живёт дальше) и «Перенести чат на другое устройство» (вторым шагом гасит
 * себя). Компонент чистый: без сети и роутера, всю работу делает вызывающий
 * через `onExport`/`onWipe` — та же граница, что у `securityPanel.jsx`,
 * благодаря ей панель живёт и в кабинете, и на публичной /j/chat.
 *
 * 🔴 Почему код показывается ОТДЕЛЬНО и с предупреждением. Файл — это полная
 * личность участника на год: identity-ключ, member-JWT и фраза стора. Утечка
 * файла ВМЕСТЕ с кодом = захват канала. Поэтому код виден только здесь, его
 * просят записать отдельно от файла, и панель об этом говорит прямо, а не
 * мелким шрифтом.
 *
 * 🔴 Почему «Закрыть чат здесь» — отдельный второй шаг, а не часть кнопки.
 * Стирание необратимо: ключи хранятся только в этом браузере. Человек должен
 * успеть сохранить файл и записать код ДО того, как исходник погаснет. Но и
 * не гасить нельзя: две живые копии одного стора расходятся по рачету, и
 * собеседник перестаёт расшифровывать обе (дизайн §2).
 */

/**
 * Ссылка на файл. Объектная, когда браузер её умеет, иначе `data:`.
 *
 * 🔴 Ветки «браузер не умеет сохранять файлы» тут быть НЕ должно. Первый
 * вариант панели её имел — и это была не тестовая мелочь, а тупик для
 * человека: без ссылки перенос не сделать вовсе, а сказать ему нечего.
 * `data:` работает везде, где работает `<a download>`, и файл наш —
 * десятки килобайт, а не гигабайты.
 */
function fileHref(text, urlRef) {
  if (typeof URL !== 'undefined' && URL.createObjectURL && typeof Blob !== 'undefined') {
    const url = URL.createObjectURL(new Blob([text], { type: 'application/octet-stream' }));
    urlRef.current = url;
    return url;
  }
  return `data:application/octet-stream;base64,${bytesToBase64(new TextEncoder().encode(text))}`;
}

const STEP_IDLE = 'idle';
const STEP_WORKING = 'working';
const STEP_READY = 'ready';
const STEP_GONE = 'gone';

export const PANEL_ID = 'msgDeviceTransferPanel';

const DeviceTransferPanel = ({ onExport, onWipe, onClose }) => {
  const [step, setStep] = useState(STEP_IDLE);
  const [kind, setKind] = useState(null);
  const [made, setMade] = useState(null); // {code, name, url}
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const closeRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => { closeRef.current?.focus(); }, []);

  // Ссылка на файл живёт в памяти вкладки: не отозвать её — держать всю
  // переписку в куче браузера до перезагрузки.
  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const make = async (what) => {
    setError(null);
    setKind(what);
    setStep(STEP_WORKING);
    try {
      const { text, code, name } = await onExport(what);
      const url = fileHref(text, urlRef);
      setMade({ code, name, url });
      setStep(STEP_READY);
    } catch (e) {
      setError(e.message || 'Не удалось собрать файл.');
      setStep(STEP_IDLE);
    }
  };

  const wipe = async () => {
    setError(null);
    setStep(STEP_WORKING);
    try {
      await onWipe();
      setStep(STEP_GONE);
    } catch (e) {
      setError(e.message || 'Не удалось закрыть чат на этом устройстве.');
      setStep(STEP_READY);
    }
  };

  return (
    <section
      className="msgTransferPanel"
      id={PANEL_ID}
      data-testid="transfer-panel"
      role="dialog"
      aria-labelledby="msgTransferPanelTitle"
      onKeyDown={(e) => { if (e.key === 'Escape' && step !== STEP_WORKING) onClose?.(); }}
    >
      <div className="msgTransferPanelHead">
        <h3 id="msgTransferPanelTitle">Копия и перенос</h3>
        <button
          type="button"
          ref={closeRef}
          onClick={onClose}
          data-testid="transfer-close"
          disabled={step === STEP_WORKING}
        >
          Закрыть
        </button>
      </div>

      {error && (
        <p className="msgTransferError" role="alert" data-testid="transfer-error">{error}</p>
      )}

      {step === STEP_GONE ? (
        <div data-testid="transfer-gone">
          <h4>Чат переехал</h4>
          <p className="msgTransferHint">
            На этом устройстве переписки больше нет. Откройте ссылку-приглашение
            на новом устройстве и выберите «Перенести чат с другого устройства».
          </p>
          <p className="msgTransferHint">
            Файл после переноса удалите — он открывает переписку целиком.
          </p>
        </div>
      ) : (
        <>
          {step !== STEP_READY && (
            <>
              <p className="msgTransferWhat">
                Переписка хранится только на этом устройстве: сервер держит
                запечатанные конверты и открыть их не может. Копия — единственный
                способ не потерять её при смене телефона или чистке браузера.
              </p>
              <div className="msgTransferActions">
                <button
                  type="button"
                  data-testid="transfer-backup"
                  disabled={step === STEP_WORKING}
                  onClick={() => make(KIND_BACKUP)}
                >
                  Сохранить копию переписки
                </button>
                <p className="msgTransferHint">
                  Устройство продолжит работать. Снимайте копию раз в неделю и
                  перед сменой телефона.
                </p>
                <button
                  type="button"
                  data-testid="transfer-move"
                  disabled={step === STEP_WORKING}
                  onClick={() => make(KIND_TRANSFER)}
                >
                  Перенести чат на другое устройство
                </button>
                <p className="msgTransferHint">
                  Вторым шагом чат на этом устройстве будет закрыт — иначе две
                  копии разойдутся и собеседник перестанет получать сообщения.
                </p>
              </div>
            </>
          )}

          {step === STEP_WORKING && (
            <p className="msgTransferHint" data-testid="transfer-working">Готовлю файл…</p>
          )}

          {step === STEP_READY && made && (
            <div data-testid="transfer-ready">
              <h4>Код переноса</h4>
              <p className="msgTransferHint">
                Запишите его и храните
                {' '}
                <strong>отдельно от файла</strong>
                . Без кода файл — набор букв; с кодом он открывает переписку
                целиком.
              </p>
              <code className="msgTransferCode" data-testid="transfer-code">{made.code}</code>

              <h4>Файл</h4>
              <a
                className="msgTransferDownload"
                data-testid="transfer-download"
                href={made.url}
                download={made.name}
                onClick={() => setSaved(true)}
              >
                Сохранить файл
              </a>
              <p className="msgTransferHint" data-testid="transfer-file-name">{made.name}</p>

              {kind === KIND_TRANSFER && (
                <>
                  <h4>Закрыть чат здесь</h4>
                  <p className="msgTransferHint">
                    {'Файл сохранён и код записан? Тогда закройте чат на этом '
                      + 'устройстве. Переписка отсюда исчезнет — она будет только '
                      + 'в файле.'}
                  </p>
                  <button
                    type="button"
                    className="msgTransferWipe"
                    data-testid="transfer-wipe"
                    disabled={!saved}
                    onClick={wipe}
                  >
                    Закрыть чат здесь
                  </button>
                  {!saved && (
                    <p className="msgTransferHint" data-testid="transfer-wipe-locked">
                      Кнопка откроется, когда файл будет сохранён.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default DeviceTransferPanel;
